import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Play, Pause, SkipForward, X } from 'lucide-react';

const STEPS = [
  // ── TEACHER ROLE ──
  { role: 'teacher', path: '/', section: 'teacher',
    title_en: 'Teacher Dashboard', title_bn: 'শিক্ষক ড্যাশবোর্ড',
    desc_en: 'Welcome to Shiksha BD. This is the teacher home screen with a personalized greeting, 6 quick action cards, attendance chart, and sync status. Everything a teacher needs in one view.',
    desc_bn: 'শিক্ষা বিডি-তে স্বাগতম। এটি শিক্ষকের হোম স্ক্রিন — ব্যক্তিগত অভিবাদন, ৬টি দ্রুত কাজ, হাজিরা চার্ট, এবং সিঙ্ক স্ট্যাটাস।',
    duration: 5000 },
  { role: 'teacher', path: '/attendance', section: 'teacher',
    title_en: 'Attendance Marking', title_bn: 'হাজিরা চিহ্নিতকরণ',
    desc_en: 'Mark student attendance with one tap — Present, Absent, Late, or Excused. Grade 1-12, Section A/B/C. GPS-verified with backdating prevention. 3+ consecutive absences auto-flag welfare alerts.',
    desc_bn: 'এক ট্যাপে হাজিরা — উপস্থিত, অনুপস্থিত, দেরিতে। শ্রেণি ১-১২, শাখা A/B/C। GPS যাচাই ও ব্যাকডেটিং প্রতিরোধ। ৩+ দিন অনুপস্থিতিতে স্বয়ংক্রিয় সতর্কতা।',
    duration: 5000 },
  { role: 'teacher', path: '/lms', section: 'teacher',
    title_en: 'My Learning — CPD & LMS', title_bn: 'আমার শিখন — CPD ও LMS',
    desc_en: 'Continuous Professional Development with NAEM-accredited courses across 5 domains: Policy, School Management, Classroom Management, Subject Pedagogy, and Technology. Quiz engine, certificates, XP leaderboard, downloadable resources, and Need Analysis for suggesting new trainings.',
    desc_bn: 'NAEM অনুমোদিত কোর্স ৫টি ডোমেইনে: নীতি, বিদ্যালয় ব্যবস্থাপনা, শ্রেণিকক্ষ ব্যবস্থাপনা, বিষয়ভিত্তিক শিক্ষাবিদ্যা, এবং প্রযুক্তি। কুইজ, সনদপত্র, XP লিডারবোর্ড, এবং প্রশিক্ষণ প্রয়োজনীয়তা বিশ্লেষণ।',
    duration: 5500 },
  { role: 'teacher', path: '/resource-hub', section: 'teacher',
    title_en: 'Resource Hub', title_bn: 'রিসোর্স হাব',
    desc_en: '6 resource types: Lesson Plans, Worksheets, Templates, Study Material, Revision Material, Question Papers. AI-powered lesson plan generator with NCTB curriculum alignment. Submit resources to Editorial Board/NAEM for district or national adoption. 5-step AI demo tutorial included.',
    desc_bn: '৬ ধরনের রিসোর্স: পাঠ পরিকল্পনা, ওয়ার্কশীট, টেমপ্লেট, অধ্যয়ন উপকরণ, রিভিশন, প্রশ্নপত্র। AI-চালিত পাঠ পরিকল্পনা তৈরি। সম্পাদনা বোর্ড/NAEM-এ জমা দেওয়ার সুবিধা।',
    duration: 5500 },
  { role: 'teacher', path: '/meals', section: 'teacher',
    title_en: 'Meals — Breakfast, Lunch, Snacks', title_bn: 'খাবার — সকালের নাস্তা, দুপুরের খাবার, স্ন্যাকস',
    desc_en: '3 meal types: Breakfast, Snacks, Lunch. Mandatory photo evidence with GPS geofencing (500m). Beneficiary count validation against enrollment. Anti-fraud anomaly detection. Weekly menu planning with automatic nutritional analysis.',
    desc_bn: '৩ ধরনের খাবার: সকালের নাস্তা, স্ন্যাকস, দুপুরের খাবার। বাধ্যতামূলক ছবি ও GPS জিওফেন্সিং (৫০০ মি.)। প্রতারণা প্রতিরোধ। সাপ্তাহিক মেনু ও পুষ্টি বিশ্লেষণ।',
    duration: 5000 },
  { role: 'teacher', path: '/health', section: 'teacher',
    title_en: 'Swasthya BD — Health Platform', title_bn: 'স্বাস্থ্য বিডি — স্বাস্থ্য প্ল্যাটফর্ম',
    desc_en: 'Private health records — blood pressure, glucose, mood tracker, burnout assessment. Telemedicine appointment booking. Mental wellness support. Women\'s health tracker. Emergency SOS (Call 999). No administrator can access your health data.',
    desc_bn: 'ব্যক্তিগত স্বাস্থ্য রেকর্ড — রক্তচাপ, গ্লুকোজ, মেজাজ ট্র্যাকার। অনলাইন ডাক্তার। মানসিক সুস্থতা। মহিলাদের স্বাস্থ্য। জরুরি SOS (৯৯৯ কল)। কোনো প্রশাসক এই তথ্য দেখতে পারবেন না।',
    duration: 5000 },
  { role: 'teacher', path: '/communication', section: 'teacher',
    title_en: 'Communication Hub', title_bn: 'যোগাযোগ কেন্দ্র',
    desc_en: '6 tabs: Community forums (Mathematics, Science, Bangla, ICT, General), Circulars with classification and ATR compliance workflow, Best Practices sharing, PTM Records for sharing reports with parents via WhatsApp/Email, Leader Board for students/teachers/principals, and Messages.',
    desc_bn: '৬টি ট্যাব: সম্প্রদায় ফোরাম, বিজ্ঞপ্তি (শ্রেণিবিভাগ ও ATR), সেরা চর্চা, PTM রেকর্ড (হোয়াটসঅ্যাপ/ইমেইলে শেয়ার), লিডারবোর্ড, এবং বার্তা।',
    duration: 5500 },
  { role: 'teacher', path: '/grades', section: 'teacher',
    title_en: 'Grades & Report Card', title_bn: 'ফলাফল ও রিপোর্ট কার্ড',
    desc_en: 'Enter marks for Periodic Test, Mid-Term, Annual Exam, and Practical assessments. Generate full Report Cards with 6 sections: Academic marks, Practical grades, Co-Curricular activities, Value-Based evaluation. Pre-populated teacher comments dropdown.',
    desc_bn: 'পর্যায়িক পরীক্ষা, মধ্যবর্ষ, বার্ষিক ও ব্যবহারিক পরীক্ষার নম্বর দিন। ৬ বিভাগ সহ সম্পূর্ণ রিপোর্ট কার্ড তৈরি করুন। পূর্ব-নির্ধারিত শিক্ষক মন্তব্য।',
    duration: 5000 },
  { role: 'teacher', path: '/career', section: 'teacher',
    title_en: 'Career Counselling', title_bn: 'ক্যারিয়ার কাউন্সেলিং',
    desc_en: 'Career development resources linked to LinkedIn Learning. 3 course categories: Career Development, Technology Skills, Student Guidance. For teachers, students, and principals. 16,000+ courses available.',
    desc_bn: 'লিংকডইন লার্নিং-এর সাথে যুক্ত ক্যারিয়ার উন্নয়ন রিসোর্স। ৩টি বিভাগ: ক্যারিয়ার উন্নয়ন, প্রযুক্তি দক্ষতা, শিক্ষার্থী নির্দেশনা। ১৬,০০০+ কোর্স।',
    duration: 4500 },
  { role: 'teacher', path: '/incidents', section: 'teacher',
    title_en: 'Student Portfolio', title_bn: 'শিক্ষার্থী পোর্টফোলিও',
    desc_en: 'Complete student profiles with 5 sections: Overview (attendance, grades, rank), Medical Records (blood group, allergies, conditions, emergency info), Academic Records, Anecdotal Records (teacher observations), and Vitals (height, weight, BMI, vision). Transferable to new schools.',
    desc_bn: '৫টি বিভাগ সহ সম্পূর্ণ প্রোফাইল: সারসংক্ষেপ, চিকিৎসা রেকর্ড, শিক্ষা রেকর্ড, ঘটনামূলক রেকর্ড, এবং শারীরিক তথ্য। নতুন বিদ্যালয়ে স্থানান্তরযোগ্য।',
    duration: 5000 },

  // ── PRINCIPAL ROLE ──
  { role: 'principal', path: '/', section: 'admin',
    title_en: 'Principal Dashboard', title_bn: 'প্রধান শিক্ষক ড্যাশবোর্ড',
    desc_en: '8 KPI stat cards: Schools, Students, Attendance Rate, Meal Compliance, Teachers, Meals Served, Anomalies, Welfare Flags. 6 report period tabs (Summary/Daily/Weekly/Monthly/Half-Yearly/Annual). Attendance chart. Open alerts panel.',
    desc_bn: '৮টি KPI কার্ড: বিদ্যালয়, শিক্ষার্থী, উপস্থিতির হার, খাবার সম্মতি, শিক্ষক, পরিবেশিত খাবার, অসঙ্গতি, কল্যাণ সতর্কতা। ৬টি প্রতিবেদন ট্যাব।',
    duration: 5000 },
  { role: 'principal', path: '/attendance', section: 'admin',
    title_en: 'Attendance Management', title_bn: 'হাজিরা ব্যবস্থাপনা',
    desc_en: '3 tabs: Attendance Report with charts and PDF export, Absence Approval workflow (3+ days → alert parent → 3 attempts → struck off → re-admit), and Attendance Leaderboard with punctuality rankings and regular latecomer tracking.',
    desc_bn: '৩টি ট্যাব: হাজিরা প্রতিবেদন, অনুপস্থিতি অনুমোদন কর্মপ্রবাহ (৩+ দিন → অভিভাবককে সতর্ক → ৩ চেষ্টা → নাম কাটা → পুনঃভর্তি), এবং হাজিরা লিডারবোর্ড।',
    duration: 5500 },
  { role: 'principal', path: '/governance', section: 'admin',
    title_en: 'Governance & Alert Management', title_bn: 'প্রশাসন ও সতর্কতা ব্যবস্থাপনা',
    desc_en: '4 tabs: Overview (cross-school comparison, KPIs), SDG4 Indicators (gender parity, disability inclusion), Alerts with resolve workflow, and Alert Management System with pipeline (Open → Investigating → Resolved → Closed), severity levels, owners, and escalation policies.',
    desc_bn: '৪টি ট্যাব: সারসংক্ষেপ, SDG4 সূচক, সতর্কতা, এবং সতর্কতা ব্যবস্থাপনা সিস্টেম (পাইপলাইন: সক্রিয় → তদন্ত → সমাধান → বন্ধ)।',
    duration: 5500 },
  { role: 'principal', path: '/grades', section: 'admin',
    title_en: 'Report Card System', title_bn: 'রিপোর্ট কার্ড সিস্টেম',
    desc_en: '3 tabs: Class Overview with rankings, individual Report Card with 6 sections (Periodic/Mid-Term/Annual/Practical/Co-Curricular/Value-Based), pre-populated teacher comments, and Analysis with subject-wise performance charts and grade distribution.',
    desc_bn: '৩টি ট্যাব: শ্রেণি সারসংক্ষেপ, ব্যক্তিগত রিপোর্ট কার্ড (৬ বিভাগ), এবং বিশ্লেষণ (বিষয়ভিত্তিক পারফরম্যান্স চার্ট ও গ্রেড বণ্টন)।',
    duration: 5000 },

  // ── MoE ROLE ──
  { role: 'moe', path: '/', section: 'national',
    title_en: 'MoE National Dashboard', title_bn: 'শিক্ষা মন্ত্রণালয় জাতীয় ড্যাশবোর্ড',
    desc_en: 'National-level view of all 64 districts. 37,489 post-primary institutions, 667,099 teachers (BANBEIS 2024). SDG4 metrics, gender parity index, disability inclusion rates. Government API integrations: Porichoy NID, BANBEIS, IEDCR, iBAS++, DGHS FHIR.',
    desc_bn: '৬৪ জেলার জাতীয় পর্যায়ের তথ্য। ৩৭,৪৮৯টি প্রতিষ্ঠান, ৬৬৭,০৯৯ জন শিক্ষক (BANBEIS ২০২৪)। SDG4 সূচক, লিঙ্গ সমতা। সরকারি API: পরিচয়, ব্যানবেইস, IEDCR, iBAS++, DGHS।',
    duration: 5000 },
  { role: 'moe', path: '/integrations', section: 'national',
    title_en: 'Government Integrations', title_bn: 'সরকারি সংযোগ',
    desc_en: '7 government API integrations with live status: Porichoy NID (Connected), BANBEIS EMIS (Connected), IEDCR Alerts (Connected), iBAS++ Payroll (Rate Limited), DGHS FHIR (Sandbox), bKash Payment, FCM Push. Latency monitoring for each service.',
    desc_bn: '৭টি সরকারি API সংযোগ: পরিচয় (সংযুক্ত), ব্যানবেইস (সংযুক্ত), IEDCR (সংযুক্ত), iBAS++ (সীমিত), DGHS (স্যান্ডবক্স), বিকাশ, FCM। প্রতিটি সেবার বিলম্ব পর্যবেক্ষণ।',
    duration: 5000 },
  { role: 'moe', path: '/manifesto', section: 'national',
    title_en: 'Manifesto Delivery Tracker', title_bn: 'ইশতেহার বাস্তবায়ন ট্র্যাকার',
    desc_en: 'Live scorecard of all 5 education pillars of the election manifesto — 5% GDP, Free Wi-Fi, OTOT, Mid-Day Meals, Clean Toilets, Free School Dress, One Child One Tree, Talent, Grants & Loans, Madrasa & TVET, Reform Commission. Every KPI is fed by teacher tablets.',
    desc_bn: 'নির্বাচনী ইশতেহারের ৫টি শিক্ষা স্তম্ভের লাইভ স্কোরকার্ড — প্রতিটি সূচক শিক্ষক ট্যাবলেট থেকে আসে।',
    duration: 6000 },
  { role: 'teacher', path: '/facilities', section: 'teacher',
    title_en: 'Clean Toilets & Vending — from the Tab', title_bn: 'ট্যাব থেকে পরিচ্ছন্ন টয়লেট ও ভেন্ডিং',
    desc_en: 'Teachers run a 5-point toilet inspection and raise vending-machine refill requests. Unresolved issues auto-escalate every 48 hours: Principal → Upazila → District → Ministry.',
    desc_bn: 'শিক্ষক ৫-পয়েন্ট টয়লেট পরিদর্শন করেন ও ভেন্ডিং মেশিন রিফিল অনুরোধ পাঠান। অমীমাংসিত সমস্যা ৪৮ ঘণ্টায় উচ্চতর স্তরে যায়।',
    duration: 5000 },
  { role: 'teacher', path: '/green', section: 'teacher',
    title_en: 'One Child, One Tree', title_bn: 'এক শিশু, এক গাছ',
    desc_en: 'Every student plants a tree; the teacher registers it with GPS and photo, does survival check-ins, and prints a certificate. Schools compete on Green Score.',
    desc_bn: 'প্রতিটি শিক্ষার্থী গাছ লাগায়; শিক্ষক জিপিএস ও ছবিসহ নিবন্ধন করেন, সনদ প্রিন্ট করেন।',
    duration: 5000 },
  { role: 'teacher', path: '/reform', section: 'teacher',
    title_en: 'Education Reform Commission', title_bn: 'শিক্ষা সংস্কার কমিশন',
    desc_en: 'The Commission consults 1.7M teachers directly on the Tab — agree / neutral / disagree with comments — and teachers review textbook chapters for NCTB.',
    desc_bn: 'কমিশন সরাসরি ট্যাবে ১৭ লক্ষ শিক্ষকের মত নেয় এবং শিক্ষক এনসিটিবির জন্য পাঠ্যপুস্তকের অধ্যায় পর্যালোচনা করেন।',
    duration: 5000 },

];

export default function DemoWalkthrough({ onClose }) {
  const { lang, setRole } = useApp();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const step = STEPS[currentStep];

  const goToStep = useCallback((idx) => {
    if (idx >= STEPS.length) { onClose(); return; }
    setCurrentStep(idx);
    setProgress(0);
    const s = STEPS[idx];
    setRole(s.role);
    setTimeout(() => navigate(s.path), 100);
  }, [navigate, setRole, onClose]);

  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { goToStep(currentStep + 1); return 0; }
        return prev + (100 / (step.duration / 100));
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, step.duration, goToStep]);

  useEffect(() => { setRole(step.role); navigate(step.path); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sectionLabel = step.section === 'teacher' ? (lang === 'bn' ? 'শিক্ষক' : 'Teacher View')
    : step.section === 'admin' ? (lang === 'bn' ? 'প্রধান শিক্ষক' : 'Principal View')
    : (lang === 'bn' ? 'মন্ত্রণালয়' : 'MoE National View');

  const sectionColor = step.section === 'teacher' ? 'var(--color-accent)' : step.section === 'admin' ? 'var(--color-warning)' : 'var(--color-error)';

  return (
    <div className="fixed inset-0 z-[200]" data-testid="demo-walkthrough">
      {/* Dark overlay — clickable to close */}
      <div className="absolute inset-0 pointer-events-auto" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }} onClick={onClose} />

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 pointer-events-auto" style={{ height: 4, background: 'rgba(255,255,255,0.15)', zIndex: 2 }}>
        <div style={{ height: '100%', background: '#F42A41', transition: 'width 100ms linear', width: `${((currentStep + progress / 100) / STEPS.length) * 100}%` }} />
      </div>

      {/* TOP PANEL — large, prominent */}
      <div className="absolute inset-0 flex items-start justify-center pointer-events-none" style={{ zIndex: 2, paddingTop: 60, paddingLeft: 24, paddingRight: 24 }}>
        <div className="pointer-events-auto" style={{ width: '90%', maxWidth: 780, maxHeight: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ background: '#006A4E', color: 'white', borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }} data-testid="walkthrough-panel">

            {/* Step dots */}
            <div style={{ display: 'flex', gap: 3, padding: '14px 24px 0' }}>
              {STEPS.map((s, i) => (
                <button key={i} onClick={() => goToStep(i)}
                  style={{ height: 4, flex: 1, borderRadius: 99, border: 'none', cursor: 'pointer', transition: 'background 200ms',
                    background: i < currentStep ? 'rgba(255,255,255,0.8)' : i === currentStep ? 'white' : 'rgba(255,255,255,0.15)' }} />
              ))}
            </div>

            <div style={{ padding: '16px 28px 20px' }}>
              {/* Section + Step counter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 13, padding: '4px 12px', borderRadius: 99, background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                  {lang === 'bn' ? 'ধাপ' : 'Step'} {currentStep + 1} / {STEPS.length}
                </span>
                <span style={{ fontSize: 13, padding: '4px 12px', borderRadius: 99, background: `${sectionColor}33`, color: sectionColor, fontWeight: 600 }}>
                  {sectionLabel}
                </span>
              </div>

              {/* Title — bilingual, LARGER */}
              <h2 style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' }}>
                {lang === 'bn' ? step.title_bn : step.title_en}
              </h2>
              <p style={{ fontSize: 13, opacity: 0.5, marginTop: 3 }}>
                {lang === 'bn' ? step.title_en : step.title_bn}
              </p>

              {/* Description — larger, more readable */}
              <p style={{ fontSize: 15, opacity: 0.8, marginTop: 12, lineHeight: 1.7, maxWidth: 620 }}>
                {lang === 'bn' ? step.desc_bn : step.desc_en}
              </p>

              {/* Controls — larger buttons */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Previous */}
                  <button onClick={() => goToStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}
                    style={{ height: 40, padding: '0 16px', borderRadius: 99, background: 'rgba(255,255,255,0.1)', border: 'none', color: currentStep === 0 ? 'rgba(255,255,255,0.25)' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                    ← {lang === 'bn' ? 'আগের' : 'Previous'}
                  </button>
                  {/* Play/Pause */}
                  <button data-testid="walkthrough-playpause" onClick={() => setIsPlaying(!isPlaying)}
                    style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                  {/* Next */}
                  <button data-testid="walkthrough-next" onClick={() => goToStep(currentStep + 1)}
                    style={{ height: 40, padding: '0 16px', borderRadius: 99, background: '#F42A41', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600 }}>
                    {currentStep < STEPS.length - 1 ? (lang === 'bn' ? 'পরবর্তী →' : 'Next →') : (lang === 'bn' ? 'শেষ করুন' : 'Finish')}
                  </button>
                </div>

                {/* Step progress bar */}
                <div style={{ flex: 1, margin: '0 20px', height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: 'white', borderRadius: 99, transition: 'width 100ms linear', width: `${progress}%` }} />
                </div>

                {/* Close */}
                <button data-testid="walkthrough-close" onClick={onClose}
                  style={{ height: 40, padding: '0 16px', borderRadius: 99, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                  <X size={14} /> {lang === 'bn' ? 'বন্ধ করুন' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
